import { ChargeBeePlanId } from '@prerender/billing-shared';
import { restApi } from '../../api/restApiSlice';

import type Plan from '../plans/Plan';
import type ScheduledChange from '../plans/ScheduledChange';

const date = new Date();
const timezoneOffsetInMinutes = date.getTimezoneOffset();

type IsPaymentInfoValidResponse = {
  data: {
    isCreditCardValid: boolean;
    isAddressValid: boolean;
    hasPaymentMethod: boolean;
  };
};

type CostEstimateResponse = {
  data: {
    renders: number;
    reservedRenders: number;
    onDemandRenders: number;
    reservedCost: number;
    onDemandCost: number;
    packagesCost: number;
    subscriptionDiscount: number | null;
    subscriptionDiscountLabel: string | null;
  };
};

type SchedulePlanCancellationResponse = {
  data: {
    cancelledAt: number;
  };
};

export const billingApiSlice = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvoices: builder.query({
      query: () => '/v3/billing/invoices',
      providesTags: ['Invoices'],
    }),
    getCostEstimate: builder.query<CostEstimateResponse, void>({
      query: () => '/v3/billing/estimation',
    }),
    getRenderExpenses: builder.query({
      query: () => ({
        url: '/v3/billing/expense/monthly',
      }),
    }),
    getInvoiceDownloadLink: builder.query({
      query: (invoiceId) => ({
        url: `/v3/billing/invoices/${invoiceId}/download-link`,
      }),
    }),
    getRenderUsage: builder.query({
      query: (numberOfDays) => ({
        url: '/v3/aggregated-metrics/renders/daily',
        params: {
          from: `-${numberOfDays}days`,
          timezoneOffset: timezoneOffsetInMinutes,
        },
      }),
    }),
    getPlans: builder.query<{ data: Plan[] }, void>({
      query: () => '/v3/plans',
      keepUnusedDataFor: Infinity, // prevent cache invalidation
      providesTags: ['Plan'],
    }),
    changePlan: builder.mutation<
      { data: Plan[] },
      {
        planId: ChargeBeePlanId;
        applyFreeTrialCoupon: boolean;
        unschedulePlanCancellation: boolean;
        activateNow?: boolean;
      }
    >({
      query: ({ planId, applyFreeTrialCoupon = false, unschedulePlanCancellation = false, activateNow }) => ({
        url: '/v3/plans',
        method: 'PATCH',
        body: { planId, applyFreeTrialCoupon, unschedulePlanCancellation, activateNow },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: plansResponse } = await queryFulfilled;
          dispatch(
            billingApiSlice.util.updateQueryData('getPlans', undefined, (draft) => {
              draft.data = plansResponse.data;
            })
          );
        } catch {
          // No-op: if mutation fails, don't update cache
        }
      },
      invalidatesTags: ['Plan'],
    }),
    schedulePlanChange: builder.mutation<void, { planId: ChargeBeePlanId; replaceScheduledActionConfirmed?: boolean }>({
      query: ({ planId, replaceScheduledActionConfirmed = false }) => ({
        url: '/v3/plans/schedule-change',
        method: 'PATCH',
        body: { planId, replaceScheduledActionConfirmed },
      }),
      invalidatesTags: ['Plan'],
    }),
    isPaymentInfoValid: builder.query<IsPaymentInfoValidResponse, void>({
      query: () => '/v3/billing/is-payment-info-valid',
    }),
    getScheduledChange: builder.query<ScheduledChange | null, void>({
      query: () => ({
        url: '/v3/plans/scheduled-change',
      }),
      transformResponse: (response: { data: ScheduledChange | null }) =>
        response.data === null
          ? null
          : {
              planId: response.data.planId,
              changeAt: new Date(response.data.changeAt),
            },
      providesTags: ['Plan'],
    }),
    requestUpgradeFromAdmins: builder.mutation<void, { requesterMessage?: string }>({
      query: ({ requesterMessage }) => ({
        url: '/v3/plans/upgrade-request',
        method: 'POST',
        body: { requesterMessage },
      }),
    }),
    sendFreeTrialDiscountEmail: builder.mutation<void, void>({
      query: () => ({
        url: '/v3/plans/free-trial-discount',
        method: 'POST',
      }),
    }),
    schedulePlanCancellation: builder.mutation<
      SchedulePlanCancellationResponse,
      { replaceScheduledActionConfirmed?: boolean } | void
    >({
      query: (args) => {
        const replaceScheduledActionConfirmed =
          typeof args === 'object' && args !== null ? Boolean(args.replaceScheduledActionConfirmed) : false;

        return {
          url: '/v3/plans/scheduled-cancellation',
          method: 'POST',
          body: { replaceScheduledActionConfirmed },
        };
      },
      invalidatesTags: ['Plan'],
    }),
    unschedulePlanCancellation: builder.mutation<void, void>({
      query: () => ({
        url: '/v3/plans/scheduled-cancellation',
        method: 'DELETE',
      }),
      invalidatesTags: ['Plan'],
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useGetCostEstimateQuery,
  useGetRenderExpensesQuery,
  useGetRenderUsageQuery,
  useLazyGetInvoiceDownloadLinkQuery,
  useGetPlansQuery,
  useIsPaymentInfoValidQuery,
  useLazyIsPaymentInfoValidQuery,
  useChangePlanMutation,
  useSchedulePlanChangeMutation,
  useLazyGetPlansQuery,
  useGetScheduledChangeQuery,
  useRequestUpgradeFromAdminsMutation,
  useSendFreeTrialDiscountEmailMutation,
  useSchedulePlanCancellationMutation,
  useUnschedulePlanCancellationMutation,
} = billingApiSlice;
